import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Cookie, Scale, Settings } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../i18n";
import { legalContent, legalUI } from "../data/legalContent";

const ICONS = { privacy: FileText, terms: Scale, cookies: Cookie };

const openCookiePrefs = () => {
    window.dispatchEvent(new CustomEvent("nida:open-cookie-prefs"));
};

export default function Legal({ type }) {
    const { lang } = useI18n();
    const u = legalUI[lang];
    const data = legalContent[type][lang];
    const Icon = ICONS[type] || FileText;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [type]);

    return (
        <div data-testid={`legal-page-${type}`} className="bg-ink-950 text-white">
            <Navbar />
            <main>
                {/* Header */}
                <section className="relative overflow-hidden pt-32 md:pt-40 pb-12 md:pb-16">
                    <div className="pointer-events-none absolute inset-0 grid-bg opacity-25 [mask-image:linear-gradient(to_bottom,#000,transparent_80%)]" />
                    <div className="pointer-events-none absolute -top-32 left-1/3 h-[28rem] w-[28rem] rounded-full bg-brand/15 blur-[140px]" />
                    <div className="container-page relative">
                        <Link
                            to="/"
                            data-testid="legal-back-home"
                            className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {u.backHome}
                        </Link>

                        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                            <div className="lg:col-span-8">
                                <span className="eyebrow">{u.eyebrow}</span>
                                <h1
                                    data-testid="legal-title"
                                    className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05]"
                                >
                                    {data.title}
                                </h1>
                                <p className="mt-4 text-sm text-white/45">{data.updated}</p>
                            </div>
                            <div className="lg:col-span-4 flex lg:justify-end">
                                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-brand-glow">
                                    <Icon className="h-6 w-6" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Body */}
                <section className="pb-24 md:pb-32">
                    <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                        {/* TOC */}
                        <aside className="lg:col-span-3">
                            <div className="lg:sticky lg:top-28 space-y-3">
                                <p className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                                    {u.eyebrow}
                                </p>
                                <LegalNavLink to="/privacidad" active={type === "privacy"}>
                                    {u.privacy}
                                </LegalNavLink>
                                <LegalNavLink to="/terminos" active={type === "terms"}>
                                    {u.terms}
                                </LegalNavLink>
                                <LegalNavLink to="/cookies" active={type === "cookies"}>
                                    {u.cookies}
                                </LegalNavLink>
                                <button
                                    type="button"
                                    onClick={openCookiePrefs}
                                    data-testid="legal-open-cookie-prefs"
                                    className="mt-4 inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
                                >
                                    <Settings className="h-3 w-3" />
                                    {u.managePrefs}
                                </button>
                            </div>
                        </aside>

                        <article className="lg:col-span-9 space-y-10 text-white/75 leading-relaxed">
                            <p className="text-base md:text-lg text-white/85 max-w-3xl">{data.intro}</p>

                            {data.sections.map((s, idx) => (
                                <section key={idx} className="border-t border-white/[0.06] pt-8">
                                    <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-white">
                                        {s.title}
                                    </h2>
                                    {s.paragraphs?.map((p, i) => (
                                        <p key={i} className="mt-4 text-sm md:text-base">
                                            {p}
                                        </p>
                                    ))}
                                    {s.list && (
                                        <ul className="mt-4 space-y-2">
                                            {s.list.map((it, i) => (
                                                <li
                                                    key={i}
                                                    className="text-sm md:text-base flex items-start gap-3"
                                                >
                                                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-glow shrink-0" />
                                                    <span>{it}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {s.table && (
                                        <div className="mt-5 overflow-x-auto">
                                            <table className="w-full text-sm border border-white/[0.06] rounded-xl overflow-hidden">
                                                <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/55">
                                                    <tr>
                                                        {s.table.head.map((h) => (
                                                            <th key={h} className="px-4 py-3 font-medium">
                                                                {h}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {s.table.rows.map((row, ri) => (
                                                        <tr key={ri} className="border-t border-white/[0.05]">
                                                            {row.map((cell, ci) => (
                                                                <td
                                                                    key={ci}
                                                                    className="px-4 py-3 align-top text-white/75"
                                                                >
                                                                    {cell}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {s.action === "openCookiePreferences" && (
                                        <button
                                            type="button"
                                            onClick={openCookiePrefs}
                                            data-testid="legal-cookie-prefs-cta"
                                            className="btn-ghost mt-5"
                                        >
                                            <Settings className="h-4 w-4" />
                                            {u.managePrefs}
                                        </button>
                                    )}
                                    {s.links && (
                                        <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {s.links.map(([label, href]) => (
                                                <li key={label}>
                                                    <a
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white/80 hover:bg-white/[0.05] hover:text-white transition"
                                                    >
                                                        {label} ↗
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {s.paragraphsAfter?.map((p, i) => (
                                        <p key={`a-${i}`} className="mt-4 text-sm md:text-base">
                                            {p}
                                        </p>
                                    ))}
                                </section>
                            ))}

                            <div className="mt-10 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 text-xs text-amber-200/85">
                                {u.disclaimer}
                            </div>
                        </article>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

function LegalNavLink({ to, active, children }) {
    return (
        <Link
            to={to}
            className={`block text-sm transition-colors ${
                active ? "text-white" : "text-white/55 hover:text-white"
            }`}
        >
            {active && <span className="inline-block mr-2 h-1 w-1 rounded-full bg-brand-glow align-middle" />}
            {children}
        </Link>
    );
}
